import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { throttle } from 'lodash';
import { useUrl } from '@/composables/useUrl';
import { useReportUtils } from '@/composables/useReportUtils';
import { useMainMenuComplexTabs } from '@/composables/useMainMenuComplexTabs';
import { useStore } from '@/store';
import { ExcludeTypeEnum } from '@/components/query/url-types';
import type { NextReportProps } from '@/components/query/url-types';
import query from '@/components/query/query-constructor';
import { hasReportRouteManualDrilldownFlag } from '@/components/query/report-route-query';
import { DrillFilterOptionsEnum } from '@/services/models/Enums';
import helpers from '@/services/helpers';
import { $emitter } from '@/services/emitter';
import type { MenuDrilldownBreadcrumbsWidths } from '@/views/reports/views/menu-drilldown/menu-drilldown-breadcrumbs.vue';

export const useMenuDrilldown = () => {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const drilldownUrl = useUrl();
  const { isReport } = useMainMenuComplexTabs();

  const drill = ref<any[]>([]);
  const menuDrilldown = ref<Element | null>(null);
  const widths = ref<MenuDrilldownBreadcrumbsWidths>(null);
  const width = ref(0);
  const drillPostponed = ref<any[]>([]);
  const buttonWidthEstimate = 120;

  const drilldown = computed(() => drilldownUrl.drilldown.value);
  const dataDecoded = computed(() => drilldownUrl.dataDecoded.value);

  const reportUtilsContext = {
    get drill() {
      return drill.value;
    },
    set drill(value: any[]) {
      drill.value = value;
    },
    getReturnLink: drilldownUrl.getReturnLink,
    get reportTabLink() {
      return drilldownUrl.reportTabLink.value;
    },
  };

  const utils = useReportUtils(reportUtilsContext);

  const getDefaultWidths = (): MenuDrilldownBreadcrumbsWidths => ({
    widths: [0],
    dividerWidth: 16,
  });

  const lastVisibleIndex = computed(() => {
    const data = widths.value || getDefaultWidths();
    let index = 0;
    let totalWidth = 0;

    while (index < data.widths.length && totalWidth < width.value) {
      const dividerWidth = index !== 0 ? data.dividerWidth : 0;
      totalWidth = totalWidth + data.widths[index] + dividerWidth;
      index = index + 1;
    }

    if (totalWidth > width.value) {
      index = index - 1;
    }

    return index || 1;
  });

  const hiddenDrill = computed(() => drillPostponed.value.slice(lastVisibleIndex.value));

  const hiddenDrillsCount = computed(() => hiddenDrill.value.length);

  const visibleDrill = computed(() => drillPostponed.value.slice(0, lastVisibleIndex.value));

  const restItems = computed(() =>
    hiddenDrill.value.map((item) => ({
      key: item.index,
      title: item.text,
      click: () => setPosition(item.index),
    })),
  );

  const recalcWidth = () => {
    if (!isReport.value || !menuDrilldown.value) return;

    const rect = menuDrilldown.value.getBoundingClientRect();
    width.value = rect.width - buttonWidthEstimate;
  };

  const onWindowResize = () => {
    recalcWidth();
  };

  const onWindowResizeThrottled = throttle(onWindowResize, 500);

  const onBusTabAdd = (reportLink: string) => {
    router.push(reportLink).then(() => {
      utils.resolveReportNavigation(drilldown.value, {
        isAddTab: true,
      });
    });
  };

  const handleBreadcrumbsResize = (value: MenuDrilldownBreadcrumbsWidths) => {
    widths.value = value;
  };

  const handleCloseDrilldown = (index: number) => {
    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      drilldownUrl.drilldownBack(index);
      $emitter.emit('reload-report');
    });
  };

  const handleSliceExclude = (item: Bemob.ReportQueryDrill) => {
    drilldownUrl.drilldownForward(null, {
      tableNextReportExclude: item.filterOptions === DrillFilterOptionsEnum.not_equals,
      tableNextReport: item.filterValues,
      tableNextReportGroup: item.filterTables,
      tableSelectedItems: [
        {
          DT_RowId: item.filterValues,
        },
      ],
      tableNextReportExcludeType: ExcludeTypeEnum.slice,
    });

    $emitter.emit('reload-report');
  };

  const setPosition = (index: number) => {
    let type = route.params.type as string;
    const report = drilldownUrl.reportQuery();
    let restorePage: Record<string, unknown> = {};

    if (report.drill) {
      const drillItems: Bemob.ReportQueryDrill[] = report.drill.splice(index);

      if (drillItems.length) {
        const tableTreeLevels = drillItems.map((item) => item.filterTables);
        $emitter.emit('restore-tree-levels', tableTreeLevels);
        type = tableTreeLevels[0];
      }

      drill.value.splice(index + 1);

      if (!report.drill.length) {
        restorePage = {
          offset: report.filterOffset,
          limit: report.filterLimit,
        };
      } else {
        const lastDrillIndex = report.drill.length - 1;

        restorePage = {
          offset: report.drill[lastDrillIndex].filterOffset,
          limit: report.drill[lastDrillIndex].filterLimit,
        };
      }
    }

    store.patchTableQueryField({
      name: 'drillPosition',
      value: index,
    });

    router
      .push({
        name: route.name ?? undefined,
        params: {
          type,
          data: query.encode({
            ...dataDecoded.value,
            ...restorePage,
          }),
          report: query.encode(report),
        },
        query: route.query,
      })
      .then(() => {
        $emitter.emit('reload-report');
      });
  };

  watch(isReport, (value) => {
    if (value) {
      helpers.afterBrowserRedraw(recalcWidth);
    }
  }, { immediate: true });

  watch(drill, (value) => {
    nextTick(() => {
      helpers.afterBrowserRedraw(() => {
        drillPostponed.value = value.map((item) => ({
          ...item,
          key: `${item.filterTables}_${item.filterValues}_${item.filterOptions}`,
        }));
      });
    });
  }, { immediate: true });

  watch(() => route.fullPath, () => {
    nextTick(() => {
      utils.resolveReportNavigation(drilldown.value, {
        isReplaceTab: hasReportRouteManualDrilldownFlag(route.params as Record<string, unknown>),
      });
    });
  });

  onMounted(() => {
    utils.resolveReportNavigation(drilldown.value, {
      isReplaceTab: false,
      isInit: true,
    });

    $emitter.on('tab-add', onBusTabAdd);
    window.addEventListener('resize', onWindowResizeThrottled);
  });

  onBeforeUnmount(() => {
    $emitter.off('tab-add', onBusTabAdd);
    window.removeEventListener('resize', onWindowResizeThrottled);
  });

  onUpdated(() => {
    const content = document.getElementById('v-breadcrumbs') as HTMLElement | null;
    if (!content) return;

    content.scrollLeft = content.scrollLeft + (content.scrollWidth - content.offsetWidth + 1);
  });

  return {
    isReport,
    drill,
    menuDrilldown,
    visibleDrill,
    hiddenDrillsCount,
    restItems,
    handleBreadcrumbsResize,
    handleCloseDrilldown,
    handleSliceExclude,
    setPosition,
  };
};
